import React, { useState } from 'react';
import { Alert, Anchor, Button, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconExclamationCircle, IconMailCheck } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import { apiErrorMessage } from '../../data/apiClient';
import { requestPasswordReset } from '../../data/PasswordAuth';
import { useCsrf } from '../../hooks/useCsrf';

/**
 * Request a password reset link.
 *
 * The success state is shown for any well-formed address, matching the API,
 * which answers identically whether or not an account exists. Saying "sent, if
 * that address is registered" only when it *is* registered would leak exactly
 * what the backend is careful not to.
 */
const PasswordRecoveryForm = () => {
    const { csrfToken } = useCsrf();
    const navigate = useNavigate();
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const form = useForm({
        initialValues: { email: '' },
        validate: {
            email: value => (/^\S+@\S+\.\S+$/.test(value.trim()) ? null : 'Enter a valid email'),
        },
    });

    const handleSubmit = async (values: typeof form.values) => {
        setSending(true);
        setError('');
        try {
            await requestPasswordReset(csrfToken, values.email.trim());
            setSent(true);
        } catch (err: any) {
            setError(apiErrorMessage(err, 'Could not send the reset link.'));
        } finally {
            setSending(false);
        }
    };

    return (
        <AuthLayout>
            <Stack gap="lg">
                <Text
                    style={{
                        fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                        lineHeight: 1.1,
                        fontWeight: 700,
                        color: 'var(--foreground)',
                    }}
                >
                    Forgot password
                </Text>

                {sent ? (
                    <>
                        <Alert
                            variant="light"
                            color="green"
                            icon={<IconMailCheck size={18} />}
                            title="Check your inbox"
                        >
                            If an account exists for that email, a password reset link is on its way.
                        </Alert>
                        <Anchor onClick={() => navigate('/sign-in')} style={{ fontWeight: 600 }}>
                            Back to sign in
                        </Anchor>
                    </>
                ) : (
                    <>
                        <Text style={{ color: 'var(--foreground-secondary)' }}>
                            Enter your email and we’ll send you a link to set a new password.
                        </Text>

                        {error && (
                            <Alert variant="light" color="red" icon={<IconExclamationCircle size={18} />}>
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={form.onSubmit(handleSubmit)}>
                            <Stack gap="sm">
                                <TextInput
                                    label="Email"
                                    placeholder="you@company.com"
                                    autoComplete="email"
                                    disabled={sending}
                                    {...form.getInputProps('email')}
                                />
                                <Button type="submit" size="md" radius={9999} loading={sending}>
                                    Send reset link
                                </Button>
                                <Anchor
                                    onClick={() => navigate('/sign-in')}
                                    style={{ fontSize: '.875rem', fontWeight: 600, alignSelf: 'flex-start' }}
                                >
                                    Back to sign in
                                </Anchor>
                            </Stack>
                        </form>
                    </>
                )}
            </Stack>
        </AuthLayout>
    );
};

export default PasswordRecoveryForm;
